import { EmergencyService } from '@app/core/auth/component/emergency-login/emergency-login.service';
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Subject, Subscription, of } from 'rxjs';
import { selectorLanguage } from '@app/core/nav/lang-switcher/lang.reducer';
import { delay, takeUntil } from 'rxjs/operators';
import { SessionStorageService, routeAnimations } from '@app/core';
import { ComponentTitleService } from '@app/shared/services/component-title.service';
import { AppConfig } from '@app/config/app.config';
import { iconCodes } from '@app/shared/cqes-icon/cqes-icon.component';
import { ConsentsService } from '@app/certificates/consents/consents.service';
import { ConsentRequest } from '@app/certificates/consents/consent';
import { ICertificateData } from '@app/certificates/manage/manage';
import { ActionCertificatesInitialize } from '@app/certificates/manage/manage.reducer';
import { ManageService } from '@app/certificates/manage/manage.service';
import { environment } from '@env/environment';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MessageOnlyQESComponent } from '@app/profile/message-only-qes/message-only-qes.component';
import { AuthenticationService } from '@app/core/auth/authentication.service';

@Component({
  selector: 'cloud-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  animations: [routeAnimations]
})
export class MainComponent implements OnInit, OnDestroy {
  componentTitle: string;
  private unsubscribe$: Subject<void> = new Subject<void>();
  readonly icons = iconCodes;
  private links = [
    {
      link: AppConfig.routes.certificates.sign,
      label: AppConfig.routes.certificates.sign,
      icon: this.icons.developer_board,
      emergencyEnabled: false,
      disabled: false
    },
    {
      link: AppConfig.routes.certificates.manage,
      label: AppConfig.routes.certificates.manage,
      icon: this.icons.screen_lock_landscape,
      emergencyEnabled: true,
      disabled: false
    },
    {
      link: AppConfig.routes.certificates.devices,
      label: AppConfig.routes.certificates.devices,
      icon: this.icons.smartphone,
      emergencyEnabled: true,
      disabled: false
    },
    {
      link: AppConfig.routes.certificates.packs,
      label: AppConfig.routes.certificates.packs,
      icon: this.icons.store,
      emergencyEnabled: false,
      disabled: false
    },
    {
      link: AppConfig.routes.certificates.history,
      label: AppConfig.routes.certificates.history,
      icon: this.icons.history,
      emergencyEnabled: false,
      disabled: false
    },
    {
      link: AppConfig.routes.certificates.consents,
      label: AppConfig.routes.certificates.consents,
      icon: this.icons.developer_board,
      emergencyEnabled: false,
      disabled: false
    },
  ];
  mainLinks = [];
  linksLoaded = false;
  certs: ICertificateData[] = [];
  consentOptions: ConsentRequest = {
    certReqIds: [],
  };
  isEmergencyLogin: boolean;
  hasBusinessAccount = false;
  businessAccountId;
  subscription: Subscription;

  constructor(
    private translateService: TranslateService,
    private componentTitleService: ComponentTitleService,
    private store: Store<any>,
    private emergency: EmergencyService,
    private consentService: ConsentsService,
    private manageService: ManageService,
    private changeRef: ChangeDetectorRef,
    private sessionStorageService: SessionStorageService,
    private router: Router,
    public dialog: MatDialog,
    public authenticationService: AuthenticationService
  ) {
    this.store
      .select(selectorLanguage)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(lang => {
        this.translateService.use(lang);
        this.translateService.setDefaultLang(lang);
        this.componentTitleService.title$.subscribe(title => {
          this.componentTitle = title;
        });
      });
    if (environment.hideOrders) {
      const index = this.links.findIndex(item => item.label === AppConfig.routes.certificates.packs);
      if (index > 0) {
        this.links.splice(index, 1);
      }
    }
  }

  ngOnInit() {
    this.getCurrentLoggedInAccount();

    this.subscription = this.sessionStorageService.businessAccountId.subscribe(id => {
      this.checkBusinessView();
    });

    this.mainLinks = this.getMainLinks();
  }

  getCurrentLoggedInAccount() {
    this.businessAccountId = this.sessionStorageService.getItem("businessAccountId");
    this.sessionStorageService.businessAccountId.next(this.businessAccountId);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.subscription.unsubscribe();
  }

  checkConsents() {
    this.linksLoaded = true;
    this.manageService.getAll().subscribe(response => {
      if (response.status == 200) {
        const certs = response.data;
        this.certs = certs;
        this.store.dispatch(new ActionCertificatesInitialize({ certificates: certs }));
        this.certs.forEach(cert => {
          this.consentOptions.certReqIds.push(cert.certReqId);
        });
        this.consentService.getAll(this.consentOptions).subscribe(r => {
          if (r.data === null || r.data.consents.length === 0) {
            // remove consents from links array
            const index = this.links.findIndex(item => item.label === AppConfig.routes.certificates.consents);
            if (index > 0) {
              this.links.splice(index, 1);
            }
          }
          this.linksLoaded = true;
        });

      } else {
        this.dialog.closeAll();
        this.openDialog()
        this.links.forEach(link => {
          if ([
            AppConfig.routes.certificates.manage,
            AppConfig.routes.certificates.devices,
            AppConfig.routes.certificates.packs,
            AppConfig.routes.certificates.history,
            AppConfig.routes.certificates.consents
          ].includes(link.link)) {
            link.disabled = true;
          }
          this.authenticationService.loginOnlyQESShowProfile(true);
        });

      }
    });
  }

  openDialog() {
    const dialogRef = this.dialog.open(MessageOnlyQESComponent, {
      panelClass: 'dialog-class-otherQES'
    });

    dialogRef.afterClosed().subscribe(result => {
    });
  }

  private getMainLinks() {
    this.checkConsents();
    // Check if emergency login is used
    this.isEmergencyLogin = this.emergency.getEmergencyState();
    // Regular authentication
    if (!this.isEmergencyLogin) {
      return this.links;
    }
    // Emergency login
    const mainLinks = [];
    this.links.forEach(element => {
      if (element.emergencyEnabled) {
        mainLinks.push(element);
      }
    });
    return mainLinks;
  }

  ngAfterViewInit(): void {
    this.changeRef.detectChanges();
  }

  prepareOutlet(o) {
    return o.isActivated && o.activatedRoute.routeConfig.path;
  }

  checkBusinessView() {
    if (this.businessAccountId && this.businessAccountId != 'personal') {
      this.router.navigateByUrl('/business');
    }
  }
}
